import { TiledGameObject } from "src/games/shared/phaserHelpers/HandyTypes";
import bindAllOfThis from "src/utils/BindThisHelper";
import { Coin } from "../objects/Coin";
import { VendingItem } from "../objects/VendingItem";
import { EndGameScene } from "./EndGameScene";
import TranslateApi from "src/utils/TranslateHelper";
import { Color } from "src/games/shared/ColorValue";
import { PhaserSlider } from "src/games/shared/Slider";

export class MainScene extends Phaser.Scene {
    gameWidth: number;
    gameHeight: number;
    map: Phaser.Tilemaps.Tilemap;
    doneButton: Phaser.GameObjects.Sprite;
    tooMuchFeedback: Phaser.GameObjects.Container;
    tooLittleFeedback: Phaser.GameObjects.Container;

    currentAmount: number;
    total: number;

    coins: Coin[];
    items: VendingItem[];
    tray: Phaser.GameObjects.Zone;

    totalCounter: Phaser.GameObjects.Text;
    machineCounter: Phaser.GameObjects.Text;
    coinZone: Phaser.GameObjects.Zone;

    boughtItemsX: number = 550;

    constructor() {
        super({
            key: "MainScene" + Math.random()
        });
        bindAllOfThis(this, MainScene.prototype);
    }

    preload(): void {
        this.load.image('background', require("/src/assets/qci/backgrounds/1e_background.png"));
        this.load.tilemapTiledJSON('map', require("/src/assets/qci/maps/vending_machine.json"));
        this.load.atlas("assets", require('src/assets/qci/atlas/gameVendingMachine.png'), require('src/assets/qci/atlas/gameVendingMachine.json'));
        this.load.spritesheet('button', require("/src/assets/qci/sprites/gameShipPainting/button.png"), { frameWidth: 90, frameHeight: 90 });
    }

    initialize() {
        this.gameWidth = this.textures.game.config.width as number;
        this.gameHeight = this.textures.game.config.height as number;
        this.map = this.make.tilemap({ key: 'map' });
        this.coins = [];
        this.items = [];
        this.total = 0;
        this.currentAmount = 0;
    }

    create(): void {
        this.initialize();
        this.createScene();
        this.registerEvents();
        this.createFeedbacks("tooMuch", "tooLittle");
    }

    registerEvents() {
        this.input.dragDistanceThreshold = 10;

        this.input.on('dragstart', (pointer: Phaser.Input.Pointer, coin: Coin) => {
            this.children.bringToTop(coin);
            coin.setScale(0.3);
        });

        this.input.on('drag', (pointer: Phaser.Input.Pointer, coin: Coin, dragX: number, dragY: number) => {
            coin.x = pointer.x;
            coin.y = pointer.y;
        });

        this.input.on('dragend', (pointer: Phaser.Input.Pointer, coin: Coin, dropped: boolean) => {
            // Move dropped object back to start if not dropped onto a dropzone
            if (!dropped) {
                coin.x = coin.input.dragStartX;
                coin.y = coin.input.dragStartY;
                coin.setScale(1);
            }
        });

        this.input.on("drop", (pointer, coin: Coin, dropzone: Phaser.GameObjects.Zone) => {
            if (dropzone.name === "tray") {
                coin.putBackInPile();
            }
            else {
                this.insertCoin(coin, dropzone);
            }
            this.updateCounters();
        });
    }

    createScene() {
        var background = this.add.image(0, 0, "background").setOrigin(0);
        var coinId = 0;
        var textStyle = {
            fontSize: 25,
            fontFamily: "Russo One, Arial",
            padding: 20,
            color: "#ffffff"
        };

        var text, box;
        this.map.getObjectLayer("Scene").objects.forEach((ob: TiledGameObject) => {
            if (ob.name === "button") {
                this.createDoneButton(ob);
            }
            else if (ob.name === "tray") {
                this.createCoinTray(ob);
            }
            else if (ob.type === "Coin") {
                var coin = new Coin(this, ob, coinId++);
                this.coins.push(coin);
                this.total += coin.value;
            }
            else if (ob.name === "machine") {
                var machineBack = this.add.sprite(ob.x, ob.y, "assets", "machine-back");
                machineBack.setOrigin(0);
                machineBack.setDepth(1);

                var machineFront = this.add.sprite(ob.x, ob.y, "assets", "machine-front");
                machineFront.setOrigin(0);
                machineFront.setDepth(3);
            }
            else if (ob.name === "counter") {
                text = this.add.text(ob.x + 20, ob.y + 15, TranslateApi.translate("VendingMachine.Phaser.yourGold"), textStyle);
                this.totalCounter = this.add.text(this.gameWidth - 170, ob.y, this.total + "", textStyle);
                this.totalCounter.setDepth(3.5);
                this.totalCounter.setFontSize(50);
            }
        });

        this.createVendingMachine(textStyle);
    }

    createVendingMachine(textStyle) {
        var itemId = 0;
        this.map.getObjectLayer("VendingMachine").objects.forEach((ob: TiledGameObject) => {
            if (ob.type === "VendingItem") {
                var item = new VendingItem(this, ob, itemId++);
                item.on("pointerdown", () => {
                    this.tryToBuyItem(item);
                });
                this.items.push(item);
            }
            else if (ob.name === "machine_count") {
                textStyle.color = "#000000";
                this.machineCounter = this.add.text(ob.x + ob.width / 2, ob.y + ob.height / 2, this.currentAmount + "", textStyle);
                this.machineCounter.setOrigin(0.5);
                this.machineCounter.setDepth(5);
            }
            else if (ob.name === "coin_zone") {
                this.coinZone = this.add.zone(ob.x + ob.width / 2, ob.y + ob.height / 2, ob.width, ob.height);
                this.coinZone.setRectangleDropZone(ob.width, ob.height);
                this.coinZone.setDepth(10);
            }
            else if (ob.name === "eject") {
                var eject = this.add.sprite(ob.x + ob.width / 2, ob.y + ob.height / 2, "assets", "eject_button");
                eject.setDepth(12);
                eject.setInteractive({ useHandCursor: true });
                eject.on("pointerdown", () => {
                    this.ejectCoins();
                });
            }
        });

    }

    tryToBuyItem(item: VendingItem) {
        if (this.currentAmount > item.cost) {
            this.showFeedback(true);
        }
        else if (this.currentAmount < item.cost) {
            this.showFeedback(false);
        }
        else {
            this.tweens.killTweensOf([this.tooLittleFeedback, this.tooMuchFeedback]);
            this.tooLittleFeedback.setAlpha(0);
            this.tooMuchFeedback.setAlpha(0);

            this.coins.forEach((c: Coin) => {
                if (c.inMachine) {
                    c.spent = true;
                }
            });
            item.free(this.boughtItemsX, 650);
            this.currentAmount = 0;
            this.updateCounters();
            this.boughtItemsX += 130;
        }
    }

    insertCoin(coin: Coin, dropzone: Phaser.GameObjects.Zone) {
        this.currentAmount += coin.value;
        this.total -= coin.value;
        coin.inMachine = true;
        coin.setVisible(false);
        this.updateCounters();
    }

    endGame() {
        this.scene.add("EndGameScene", EndGameScene, true);
        this.scene.remove(this);
    }

    createDoneButton(ob: TiledGameObject) {
        this.doneButton = this.add.sprite(ob.x, ob.y, "button", 1);
        this.doneButton.setOrigin(0);
        this.doneButton.setDepth(3);
        this.doneButton.setInteractive({ useHandCursor: true });

        this.doneButton.on('pointerover', () => {
            this.doneButton.setFrame(0);
        });

        this.doneButton.on('pointerout', () => {
            this.doneButton.setFrame(1);
        });

        this.doneButton.on("pointerdown", () => {
            this.doneButton.setFrame(0);
            this.endGame();
        });

        var style = { fontFamily: "Russo One, Arial", fontSize: 18, color: "#000000" };
        var text = this.add.text(ob.x, this.doneButton.y, TranslateApi.translate("VendingMachine.Phaser.save"), style);
        Phaser.Display.Align.In.TopCenter(text, this.doneButton);
        text.y -= 30;
    }

    createCoinTray(ob: TiledGameObject) {
        this.tray = this.add.zone(ob.x, ob.y, ob.width, ob.height);
        this.tray.setOrigin(0);
        this.tray.setDepth(2);

        // Tray is also a dropzone for the coins 
        this.tray.setName("tray");
        this.tray.setRectangleDropZone(ob.width, ob.height);
    }

    createFeedbacks(key1: string, key2: string) {
        var style = { fontSize: 18, fontFamily: "Russo One, Arial", padding: 10, align: "center" };
        var tooMuchText = this.add.text(0, 0, TranslateApi.translate("VendingMachine.Phaser." + key1), style);
        tooMuchText.setOrigin(0.5);
        tooMuchText.setWordWrapWidth(300, true);
        var tooMuchBackground = this.add.image(0, 0, "pixel").setTint(0).setDisplaySize(tooMuchText.width + 20, tooMuchText.height + 20);
        var tooMuchBorder = this.add.image(0, 0, "pixel").setTint(Color.PLANET_COLORS.get("E")).setDisplaySize(tooMuchBackground.displayWidth + 5, tooMuchBackground.displayHeight + 5);
        this.tooMuchFeedback = this.add.container(0, 0, [tooMuchBorder, tooMuchBackground, tooMuchText]);
        this.tooMuchFeedback.setPosition(this.gameWidth / 2 - this.tooMuchFeedback.displayWidth / 2, this.gameHeight / 2 - this.tooMuchFeedback.displayHeight / 2);
        this.tooMuchFeedback.setAlpha(0);
        this.tooMuchFeedback.setDepth(5);

        var tooLittleText = this.add.text(0, 0, TranslateApi.translate("VendingMachine.Phaser." + key2), style);
        tooLittleText.setOrigin(0.5);
        tooLittleText.setWordWrapWidth(300, true);
        var tooLittleBackground = this.add.image(0, 0, "pixel").setTint(0).setDisplaySize(tooLittleText.width + 20, tooLittleText.height + 20);
        var tooLittleBorder = this.add.image(0, 0, "pixel").setTint(Color.PLANET_COLORS.get("E")).setDisplaySize(tooLittleBackground.displayWidth + 5, tooLittleBackground.displayHeight + 5);
        this.tooLittleFeedback = this.add.container(0, 0, [tooLittleBorder, tooLittleBackground, tooLittleText]);
        this.tooLittleFeedback.setPosition(this.gameWidth / 2 - this.tooLittleFeedback.displayWidth / 2, this.gameHeight / 2 - this.tooLittleFeedback.displayHeight / 2);
        this.tooLittleFeedback.setAlpha(0);
        this.tooLittleFeedback.setDepth(5);
    }

    showFeedback(tooMuch: boolean) {
        var container;
        this.tweens.killTweensOf([this.tooLittleFeedback, this.tooMuchFeedback]);

        if (tooMuch) {
            container = this.tooMuchFeedback;
            this.tooLittleFeedback.setAlpha(0);
        }
        else {
            container = this.tooLittleFeedback;
            this.tooMuchFeedback.setAlpha(0);
        }

        container.setPosition(700, 100);
        container.setAlpha(1);
        this.tweens.add({
            targets: container,
            delay: 2000,
            duration: 1000,
            alpha: {
                getStart: () => 1,
                getEnd: () => 0,
            },
        });
    }

    updateCounters() {
        this.totalCounter.setText(this.total + "");
        this.machineCounter.setText(this.currentAmount + "");
    }

    ejectCoins() {
        this.coins.forEach((c: Coin) => {
            if (c.inMachine && !c.spent) {
                c.putBackInPile();
                c.setVisible(true);
                this.currentAmount -= c.value;
                this.total += c.value;
            }
        });

        this.updateCounters();
    }
}



// WEBPACK FOOTER //
// ./src/games/VendingMachine/scenes/MainScene.ts